// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Rafael Pereira Satyro

import type { StepComponentProps } from '../../types/wizard';
import type { PolicyTypeKey } from '../../types/wizard';
import { FileUploadField } from '../shared/FileUploadField';

// ========================================================================
// Upload visibility matrix per investment type (from Excel specification)
// ========================================================================

interface UploadSection {
  fieldKey: string;
  label: string;
  types: readonly PolicyTypeKey[];
}

const UPLOAD_SECTIONS: readonly UploadSection[] = [
  {
    fieldKey: 'valuationUpload',
    label: 'Valuation (showing full breakdown and asset allocation of the fund)',
    types: ['INVESTMENT_BOND', 'REGULAR_SAVER', 'CHILDS_SAVER', 'BARE_TRUST', 'SECTION_73', 'TRADING_ACCOUNTS', 'INVESTMENT_FUND', 'INVESTMENT_PORTFOLIO', 'STRUCTURED_PRODUCTS'],
  },
  {
    fieldKey: 'statementOfAccountUpload',
    label: 'Statement of account since inception',
    types: ['INVESTMENT_BOND', 'REGULAR_SAVER', 'CHILDS_SAVER', 'BARE_TRUST', 'SECTION_73', 'TRADING_ACCOUNTS', 'INVESTMENT_FUND', 'INVESTMENT_PORTFOLIO', 'STRUCTURED_PRODUCTS'],
  },
  {
    fieldKey: 'termsOfInvestmentUpload',
    label: 'Terms of Investment Document',
    types: ['STRUCTURED_PRODUCTS'],
  },
  {
    fieldKey: 'policyDocumentUpload',
    label: 'Policy Document',
    types: ['CHILDS_SAVER', 'BARE_TRUST', 'SECTION_73'],
  },
  {
    fieldKey: 'mifidCostsChargesUpload',
    label: 'Mifid Costs & Charges Report',
    types: ['TRADING_ACCOUNTS', 'INVESTMENT_FUND', 'INVESTMENT_PORTFOLIO', 'STRUCTURED_PRODUCTS'],
  },
  {
    fieldKey: 'mostRecentTaxPackUpload',
    label: 'Most recent Tax Pack',
    types: ['TRADING_ACCOUNTS', 'INVESTMENT_FUND', 'INVESTMENT_PORTFOLIO', 'STRUCTURED_PRODUCTS'],
  },
];

// ========================================================================
// Component
// ========================================================================

interface StepUploadsProps extends StepComponentProps {
  policyType?: PolicyTypeKey | null;
}

export function StepUploads({
  pendingFiles,
  onAddFile,
  onRemoveFile,
  policyType,
}: StepUploadsProps) {
  const visibleSections = UPLOAD_SECTIONS.filter(
    (s) => s.types.length > 0 && (!policyType || (s.types as readonly string[]).includes(policyType)),
  );

  return (
    <div className="flex flex-col gap-lg">
      <h2 className="text-black font-sans text-h5 font-normal leading-normal not-italic">
        Uploads
      </h2>

      {visibleSections.map((section, index) => (
        <div key={section.fieldKey}>
          <div className="text-black font-sans text-body-02 font-bold leading-normal not-italic">
            {section.label}
          </div>
          <div className="mt-sm">
            <FileUploadField
              fieldKey={section.fieldKey}
              files={pendingFiles?.get(section.fieldKey)}
              onAddFile={onAddFile}
              onRemoveFile={onRemoveFile}
            />
          </div>
          {index < visibleSections.length - 1 && (
            <div className="border-t border-[#E0E0E0] mt-lg" />
          )}
        </div>
      ))}
    </div>
  );
}
